import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  SavedSearch,
  SavedSearchListResponse,
  CreateSavedSearchRequest,
  UpdateSavedSearchRequest,
  RenameSavedSearchRequest
} from '../interfaces/saved-search.interface';
import { EnvironmentService } from './environment.service';

@Injectable({
  providedIn: 'root'
})
export class SavedSearchService {

  constructor(
    private http: HttpClient,
    private env: EnvironmentService,
    private router: Router
  ) {}

  /**
   * Build the base URL for saved search API
   */
  private getBaseUrl(): string {
    const envValue: any = this.env.getEnvironment();
    return envValue.baseUrl + envValue.wso2amEndPoint + '/rdm' + envValue.wso2amApiVersion + '/api/v1/saved-search';
  }

  /**
   * Get dashboard ID from current route
   * Returns the route path formatted as dashboard ID
   * Example: /app/user-mgmt/user-list -> user-mgmt_user-list
   */
  getDashboardIdFromRoute(): string {
    const currentUrl = this.router.url;

    // Remove leading slash and query params
    let cleanPath = currentUrl.split('?')[0].replace(/^\//, '');

    // Remove 'app/' prefix if present
    cleanPath = cleanPath.replace(/^app\//, '');

    // Replace slashes with underscores
    const dashboardId = cleanPath.replace(/\//g, '_');

    return dashboardId || 'default';
  }

  /**
   * Get default saved search for a user and dashboard
   * GET /rdm/api/v1/saved-search/{dashboardId}/default
   */
  getDefaultSavedSearch(dashboardId?: string): Observable<SavedSearch | null> {
    const id = dashboardId || this.getDashboardIdFromRoute();
    return this.http.get<SavedSearch>(`${this.getBaseUrl()}/${id}/default`).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Get all saved searches for a user and dashboard with pagination and sorting
   * GET /rdm/api/v1/saved-search/{dashboardId}?page=0&size=10&sort=queryName,asc
   */
  getAllSavedSearches(dashboardId?: string, page: number = 0, size: number = 10, queryName?:string,sort?: string): Observable<SavedSearchListResponse> {
    const id = dashboardId || this.getDashboardIdFromRoute();
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (queryName) {
      params = params.set('queryName', queryName);
    }

    if (sort) {
      params = params.set('sort', sort);
    }

    return this.http.get<SavedSearchListResponse>(`${this.getBaseUrl()}/${id}`, { params });
  }

  /**
   * Get saved search by ID
   * GET /rdm/api/v1/saved-search/{dashboardId}/{id}
   */
  getSavedSearchById(searchId: number, dashboardId?: string): Observable<SavedSearch> {
    const id = dashboardId || this.getDashboardIdFromRoute();
    return this.http.get<SavedSearch>(`${this.getBaseUrl()}/${id}/${searchId}`);
  }

  /**
   * Create a new saved search
   * POST /rdm/api/v1/saved-search/{dashboardId}
   */
  createSavedSearch(request: CreateSavedSearchRequest, dashboardId?: string): Observable<SavedSearch> {
    const id = dashboardId || this.getDashboardIdFromRoute();
    return this.http.post<SavedSearch>(`${this.getBaseUrl()}/${id}`, request);
  }

  /**
   * Update an existing saved search
   * PUT /rdm/api/v1/saved-search/{dashboardId}/{id}
   */
  updateSavedSearch(searchId: number, request: UpdateSavedSearchRequest, dashboardId?: string): Observable<SavedSearch> {
    const id = dashboardId || this.getDashboardIdFromRoute();
    return this.http.put<SavedSearch>(`${this.getBaseUrl()}/${id}/${searchId}`, request);
  }

  /**
   * Rename a saved search
   * PATCH /rdm/api/v1/saved-search/{dashboardId}/{id}/rename
   */
  renameSavedSearch(searchId: number, newName: string, dashboardId?: string): Observable<SavedSearch> {
    const id = dashboardId || this.getDashboardIdFromRoute();
    const request: RenameSavedSearchRequest = { queryName: newName };
    return this.http.patch<SavedSearch>(`${this.getBaseUrl()}/${id}/${searchId}/rename`, request);
  }

  /**
   * Set a saved search as default
   * PATCH /rdm/api/v1/saved-search/{dashboardId}/{id}/default
   */
  setDefaultSavedSearch(searchId: number, dashboardId?: string): Observable<SavedSearch> {
    const id = dashboardId || this.getDashboardIdFromRoute();
    return this.http.patch<SavedSearch>(`${this.getBaseUrl()}/${id}/${searchId}/default`, {});
  }

  /**
   * Delete a saved search
   * DELETE /rdm/api/v1/saved-search/{dashboardId}/{id}
   */
  deleteSavedSearch(searchId: number, dashboardId?: string): Observable<void> {
    const id = dashboardId || this.getDashboardIdFromRoute();
    return this.http.delete<void>(`${this.getBaseUrl()}/${id}/${searchId}`);
  }

  /**
   * Helper method to prepare search criteria for saving
   * Converts form values to JSON string
   */
  stringifySearchCriteria(formValues: any): string {
    return JSON.stringify(formValues);
  }

  /**
   * Helper method to parse saved search criteria
   * Converts JSON string back to object
   */
  parseSearchCriteria(criteriaString: string): any {
    try {
      return JSON.parse(criteriaString);
    } catch {
      return {};
    }
  }
}
